import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PdfActionButton from "../PdfActionButton";
import useAuth from "../../hooks/useAuth";
import { UPDATE_PROJECT_MUTATION } from "../../constants/graphql/mutations";

const LABEL_TYPE_OPTIONS = [
  { value: "standard",   label: "Standard" },
  { value: "mini",       label: "Mini" },
  { value: "move_label", label: "Picture Label" },
];

const COUNT_BY_OPTIONS = [
  { value: "rack_location", label: "By Item ID" },
  { value: "qty",           label: "Per Unit" },
];

const FULL_PAGE_VALUE = "full_page";

// Maps the frontend size key to the backend label_type param.
const LABEL_TYPE_MAP = {
  standard:   "full_label",
  mini:       "partial_label",
  move_label: "combined_move_label",
};

const getLabelType = size => LABEL_TYPE_MAP[size] || "full_label";

const styles = {
  closeButton: {
    position: "absolute",
    right: 0,
    top: 0,
  },
  moreOptionsButton: {
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 500,
    color: "text.secondary",
    p: 0,
    minWidth: 0,
    "&:hover": { background: "none", textDecoration: "underline" },
  },
};

export default function LabelingOptionsModal({ onClose, shipmentId, shipmentItemId, project }) {
  const { user } = useAuth();
  const [updateProject] = useMutation(UPDATE_PROJECT_MUTATION);

  const backendPreferences = project?.shipmentItemPdfPreferences || {};
  const [labelSize,          setLabelSize]          = useState(backendPreferences.size       || "standard");
  const [countBy,            setCountBy]            = useState(backendPreferences.count_by   || "rack_location");
  const [selectedSheetTemplate, setSelectedSheetTemplate] = useState(
    backendPreferences.sheet_template || FULL_PAGE_VALUE
  );
  const [sheetTemplates,    setSheetTemplates]    = useState([]);
  const [showMoreOptions,   setShowMoreOptions]   = useState(false);

  useEffect(() => {
    if (sheetTemplates.length > 0) return;
    fetch(`/label_sheet_templates?token=${user.token}`)
      .then(r => r.json())
      .then(data => setSheetTemplates(data.templates || []))
      .catch(() => {});
  }, [sheetTemplates.length, user.token]);

  const persistPreferencesIfChanged = async () => {
    const changed =
      labelSize  !== backendPreferences.size      ||
      countBy    !== backendPreferences.count_by  ||
      selectedSheetTemplate !== (backendPreferences.sheet_template || FULL_PAGE_VALUE);

    if (!changed || !project?.id) return;

    const shipmentItemPdfPreferences = {
      size:          labelSize,
      count_by:      countBy,
      sheet_template: selectedSheetTemplate,
    };
    await updateProject({ variables: { id: project.id, name: project.name, shipmentItemPdfPreferences } });
  };

  const itemLabelQueryParams = () => {
    const extraQueryParams = {
      item_lables_count_by: countBy,
      label_type: getLabelType(labelSize),
    };

    if (shipmentItemId) {
      extraQueryParams.shipment_item_id = shipmentItemId;
    }

    if (selectedSheetTemplate && selectedSheetTemplate !== FULL_PAGE_VALUE) {
      extraQueryParams.sheet_template = selectedSheetTemplate;
    }

    return extraQueryParams;
  };

  const handleGenerateItemLabels = () => {
    onClose();
    persistPreferencesIfChanged();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box position="relative">
          <Typography fontSize={22} fontWeight={500} color="text.secondary" align="center">
            Item Label Options
          </Typography>
          <IconButton onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box py={1} display="flex" flexDirection="column" gap={2.5}>
          <Typography sx={{ textAlign: "center", fontSize: "16px", color: "text.primary", fontWeight: 400, lineHeight: 1.5 }}>
            Choose how you'd like your item labels generated
          </Typography>

          {/* Label Template */}
          <FormControl fullWidth size="small">
            <InputLabel>Label Template</InputLabel>
            <Select
              value={labelSize}
              label="Label Template"
              onChange={e => setLabelSize(e.target.value)}
            >
              {LABEL_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Page Setup — grouped by printer type */}
          <FormControl fullWidth size="small">
            <InputLabel>Page Setup</InputLabel>
            <Select
              value={selectedSheetTemplate}
              label="Page Setup"
              onChange={e => setSelectedSheetTemplate(e.target.value)}
            >
              <ListSubheader>Letter Sized Sheets</ListSubheader>
              <MenuItem value={FULL_PAGE_VALUE}>8½ × 11&quot; (1 per sheet)</MenuItem>
              {sheetTemplates.filter(t => t.printer_type === "sheet").map(t => (
                <MenuItem key={t.key} value={t.key}>{t.name}</MenuItem>
              ))}
              <ListSubheader>Thermal Printer Labels (Dymo)</ListSubheader>
              {sheetTemplates.filter(t => t.printer_type === "dymo").map(t => (
                <MenuItem key={t.key} value={t.key}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* More Options toggle */}
          <Box>
            <Button
              disableRipple
              onClick={() => setShowMoreOptions(v => !v)}
              endIcon={showMoreOptions ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              sx={styles.moreOptionsButton}
            >
              More Options
            </Button>

            {showMoreOptions && (
              <Box mt={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Count by</InputLabel>
                  <Select
                    value={countBy}
                    label="Count by"
                    onChange={e => setCountBy(e.target.value)}
                  >
                    {COUNT_BY_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <PdfActionButton
          resourceType="shipment_items_pdfs"
          resourceId={shipmentId}
          extraQueryParams={itemLabelQueryParams()}
          onOpenComplete={handleGenerateItemLabels}
          variant="contained"
          color="primary"
        >
          Generate Item Labels
        </PdfActionButton>
      </DialogActions>
    </Dialog>
  );
}
